import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/admin/CouponForm.jsx
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Typography,
    Alert,
    Box,
    InputAdornment,
    Chip
} from '@mui/material';

const CouponForm = ({ open, onClose, onSave, coupon }) => {
  const { t } = useTranslation();
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        description: '',
        discount_type: 'percentage',
        discount_value: '',
        usage_limit: '',
        usage_limit_per_user: '',
        valid_from: '',
        valid_until: '',
        is_active: true,
        minimum_amount: '',
        applies_to: t('hardcoded.components_admin_couponform.subtotal'),
        before_or_after_tax: 'before_tax',
        stackable: false,
        applicable_products: [],
        applicable_categories: []
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (coupon) {
            // Convertir fechas ISO a datetime-local format
            const formatDateForInput = (isoDate) => {
                if (!isoDate) return '';
                const date = new Date(isoDate);
                return date.toISOString().slice(0, 16);
            };

            setFormData({
                code: coupon.code || '',
                name: coupon.name || '',
                description: coupon.description || '',
                discount_type: coupon.discount_type || t('hardcoded.components_admin_couponform.percentage'),
                discount_value: coupon.discount_value?.toString() || '',
                usage_limit: coupon.usage_limit?.toString() || '',
                usage_limit_per_user: coupon.usage_limit_per_user?.toString() || '',
                valid_from: formatDateForInput(coupon.valid_from),
                valid_until: formatDateForInput(coupon.valid_until),
                is_active: coupon.is_active !== undefined ? coupon.is_active : true,
                minimum_amount: coupon.minimum_amount?.toString() || '',
                applies_to: coupon.applies_to || t('hardcoded.components_admin_couponform.subtotal'),
                before_or_after_tax: coupon.before_or_after_tax || 'before_tax',
                stackable: coupon.stackable || false,
                applicable_products: coupon.applicable_products || [],
                applicable_categories: coupon.applicable_categories || []
            });
        } else {
            // Para nuevo cupón, establecer fecha actual
            const now = new Date();
            setFormData({
                code: '',
                name: '',
                description: '',
                discount_type: 'percentage',
                discount_value: '',
                usage_limit: '',
                usage_limit_per_user: '',
                valid_from: now.toISOString().slice(0, 16),
                valid_until: '',
                is_active: true,
                minimum_amount: '',
                applies_to: t('hardcoded.components_admin_couponform.subtotal'),
                before_or_after_tax: 'before_tax',
                stackable: false,
                applicable_products: [],
                applicable_categories: []
            });
        }
        setErrors({});
    }, [coupon, open]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        if (field === 'name' && !coupon && !formData.code) {
            const code = value
                .toUpperCase()
                .trim()
                .replace(/[^A-Z0-9\s]/g, '')
                .replace(/\s+/g, '')
                .substring(0, 12);
            setFormData(prev => ({ ...prev, code }));
        }

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.code.trim()) {
            newErrors.code = t('hardcoded.components_admin_couponform.codigo_es_requerido');
        } else if (!/^[A-Z0-9]+$/.test(formData.code)) {
            newErrors.code = t('hardcoded.components_admin_couponform.codigo_solo_puede_contener_letras');
        }

        if (!formData.name.trim()) {
            newErrors.name = t('hardcoded.components_admin_categoryform.nombre_es_requerido');
        }

        if (!formData.discount_value || parseFloat(formData.discount_value) <= 0) {
            newErrors.discount_value = t('hardcoded.components_admin_couponform.valor_de_descuento_debe_ser');
        }

        if (formData.discount_type === 'percentage' && parseFloat(formData.discount_value) > 100) {
            newErrors.discount_value = t('hardcoded.components_admin_couponform.porcentaje_no_puede_ser_mayor');
        }

        if (formData.usage_limit && parseInt(formData.usage_limit) <= 0) {
            newErrors.usage_limit = t('hardcoded.components_admin_couponform.limite_de_uso_debe_ser');
        }

        if (formData.usage_limit_per_user && parseInt(formData.usage_limit_per_user) <= 0) {
            newErrors.usage_limit_per_user = t('hardcoded.components_admin_couponform.limite_por_usuario_debe_ser');
        }

        if (formData.minimum_amount && parseFloat(formData.minimum_amount) < 0) {
            newErrors.minimum_amount = t('hardcoded.components_admin_couponform.monto_minimo_no_puede_ser');
        }

        if (!formData.valid_from) {
            newErrors.valid_from = t('hardcoded.components_admin_couponform.fecha_de_inicio_es_requerida');
        }

        if (formData.valid_until && formData.valid_from) {
            const validFrom = new Date(formData.valid_from);
            const validUntil = new Date(formData.valid_until);
            if (validUntil < validFrom) {
                newErrors.valid_until = t('hardcoded.components_admin_couponform.fecha_de_vencimiento_debe_ser');
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validateForm()) return;

        const saveData = {
            ...formData,
            code: formData.code.toUpperCase().trim(),
            discount_value: parseFloat(formData.discount_value),
            usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
            usage_limit_per_user: formData.usage_limit_per_user ? parseInt(formData.usage_limit_per_user) : null,
            minimum_amount: formData.minimum_amount ? parseFloat(formData.minimum_amount) : null,
            valid_from: new Date(formData.valid_from).toISOString(),
            valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null
        };

        onSave(saveData);
    };

    const generateRandomCode = () => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 8; i++) {
            code += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        setFormData(prev => ({ ...prev, code }));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {coupon ? t('hardcoded.components_admin_couponform.editar_cupon') : t('hardcoded.components_admin_couponform.crear_cupon')}
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>{t('audit.detail.basicInfo')}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_couponform.codigo_del_cupon')}
                            value={formData.code}
                            onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                            error={!!errors.code}
                            helperText={errors.code || t('hardcoded.components_admin_couponform.solo_letras_mayusculas_y_numeros')}
                            InputProps={{
                                endAdornment: !coupon && (
                                    <InputAdornment position="end">
                                        <Button size="small" onClick={generateRandomCode}>
                                            {i18n.t('hardcoded.components_admin_couponform.generar')}</Button>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_couponform.nombre_del_cupon')}
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('audit.table.description')}
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            multiline
                            rows={2}
                            helperText={t('hardcoded.components_admin_couponform.descripcion_visible_para_los_usuarios')}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>{t('hardcoded.components_admin_couponform.configuracion_del_descuento')}</Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.components_admin_couponform.tipo_de_descuento')}</InputLabel>
                            <Select
                                value={formData.discount_type}
                                label={t('hardcoded.components_admin_couponform.tipo_de_descuento')}
                                onChange={(e) => handleChange('discount_type', e.target.value)}
                            >
                                <MenuItem value="percentage">{i18n.t('hardcoded.components_admin_couponform.porcentaje')}</MenuItem>
                                <MenuItem value="fixed">{t('hardcoded.components_admin_couponform.cantidad_fija')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_couponform.valor_del_descuento')}
                            type="number"
                            value={formData.discount_value}
                            onChange={(e) => handleChange('discount_value', e.target.value)}
                            error={!!errors.discount_value}
                            helperText={errors.discount_value}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {formData.discount_type === 'percentage' ? '%' : '$'}
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.components_admin_couponform.aplica_sobre')}</InputLabel>
                            <Select
                                value={formData.applies_to}
                                label={t('hardcoded.components_admin_couponform.aplica_sobre')}
                                onChange={(e) => handleChange('applies_to', e.target.value)}
                            >
                                <MenuItem value="subtotal">{i18n.t('hardcoded.components_admin_couponform.subtotal')}</MenuItem>
                                <MenuItem value="total">{i18n.t('common.total')}</MenuItem>
                                <MenuItem value="tax">IVA</MenuItem>
                                <MenuItem value="shipping">{t('hardcoded.components_admin_couponform.envio')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.components_admin_couponform.momento_de_calculo')}</InputLabel>
                            <Select
                                value={formData.before_or_after_tax}
                                label={t('hardcoded.components_admin_couponform.momento_de_calculo')}
                                onChange={(e) => handleChange('before_or_after_tax', e.target.value)}
                            >
                                <MenuItem value="before_tax">{t('hardcoded.components_admin_couponform.antes_de_iva')}</MenuItem>
                                <MenuItem value="after_tax">{t('hardcoded.components_admin_couponform.despues_de_iva')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_couponform.compra_minima')}
                            type="number"
                            value={formData.minimum_amount}
                            onChange={(e) => handleChange('minimum_amount', e.target.value)}
                            error={!!errors.minimum_amount}
                            helperText={errors.minimum_amount || t('hardcoded.components_admin_couponform.monto_minimo_para_aplicar_el')}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>{t('hardcoded.components_admin_couponform.limites_de_uso')}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_couponform.limite_total_de_usos')}
                            type="number"
                            value={formData.usage_limit}
                            onChange={(e) => handleChange('usage_limit', e.target.value)}
                            error={!!errors.usage_limit}
                            helperText={errors.usage_limit || t('hardcoded.components_admin_couponform.dejar_vacio_para_uso_ilimitado')}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_couponform.limite_por_usuario')}
                            type="number"
                            value={formData.usage_limit_per_user}
                            onChange={(e) => handleChange('usage_limit_per_user', e.target.value)}
                            error={!!errors.usage_limit_per_user}
                            helperText={errors.usage_limit_per_user || t('hardcoded.components_admin_couponform.usos_maximos_por_usuario')}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>{t('hardcoded.components_admin_couponform.periodo_de_validez')}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_couponform.valido_desde')}
                            type="datetime-local"
                            value={formData.valid_from}
                            onChange={(e) => handleChange('valid_from', e.target.value)}
                            error={!!errors.valid_from}
                            helperText={errors.valid_from}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.components_admin_couponform.valido_hasta')}
                            type="datetime-local"
                            value={formData.valid_until}
                            onChange={(e) => handleChange('valid_until', e.target.value)}
                            error={!!errors.valid_until}
                            helperText={errors.valid_until || t('hardcoded.components_admin_couponform.dejar_vacio_para_que_no')}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>{i18n.t('gps.table.status')}</Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) => handleChange('is_active', e.target.checked)}
                                />
                            }
                            label={t('hardcoded.components_admin_couponform.cupon_activo')}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.stackable}
                                    onChange={(e) => handleChange('stackable', e.target.checked)}
                                />
                            }
                            label={t('hardcoded.components_admin_couponform.acumulable_con_otros_cupones')}
                        />
                    </Grid>

                    {formData.code && formData.discount_value && (
                        <Grid item xs={12}>
                            <Alert severity="info">
                                <Typography variant="body2">
                                    <strong>{i18n.t('hardcoded.components_admin_couponform.vista_previa')}</strong> {t('hardcoded.components_admin_couponform.el_cupon')}{formData.code}{t('hardcoded.components_admin_couponform.ofrecera')}{' '}
                                    {formData.discount_type === 'percentage'
                                        ? `${formData.discount_value}% de descuento`
                                        : t('hardcoded.components_admin_couponform.discount_value_de_descuento', { discount_value: formData.discount_value })
                                    }
                                    {formData.minimum_amount && t('hardcoded.components_admin_couponform.en_compras_mayores_a_minimum_amount', { minimum_amount: formData.minimum_amount })}
                                    .
                                </Typography>
                            </Alert>
                        </Grid>
                    )}

                    {coupon && (
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>{t('hardcoded.components_admin_couponform.estadisticas_de_uso')}</Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    label={t('hardcoded.components_admin_couponform.usado_couponusage_cou_veces', { couponusage_cou: coupon.usage_count || 0 })}
                                    color="primary"
                                    variant="outlined"
                                />
                                {formData.usage_limit && (
                                    <Chip
                                        label={t('hardcoded.components_admin_couponform.formdatausage_l_usos_restantes', { formDatausage_l: formData.usage_limit - (coupon.usage_count || 0) })}
                                        color={formData.usage_limit - (coupon.usage_count || 0) > 0 ? 'success' : 'error'}
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button onClick={handleSave} variant="contained">
                    {coupon ? t('audit.actions.UPDATE') : t('audit.actions.CREATE')} {t('hardcoded.components_admin_couponform.cupon')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CouponForm;